/* eslint-disable no-param-reassign */
/* eslint-disable no-undef */
import cookies from 'js-cookie';
import Prism from './prism';

import './styles/codeblock.scss';

export default () => (editor) => {
  const { vm, type } = editor.props;
  let textMode = type !== 'course-builder' || vm.selectedMode === 'text';
  let codeMode = type === 'course-builder' && vm.selectedMode === 'code' && vm.mode === 'answer';
  const editorElement = editor.getElement();

  if (editorElement) {
    editorElement.addEventListener('mode-change', (e) => {
      const { mode, selectedMode } = e.detail;
      textMode = type !== 'course-builder' || mode === 'text';
      codeMode = type === 'course-builder' && mode === 'code' && selectedMode === 'code';
    });
  }

  const hideCaret = () => {
    editor.bodyElement.style['caret-color'] = 'transparent';
  };

  const showCaret = () => {
    editor.bodyElement.style['caret-color'] = 'initial';
  };

  const getNearestSiblings = (element) => {
    // Get elements before and after the caret
    // When the caret is inside p tag, then get before/after elements of the p tag
    const object = {};
    const parentNode = element.parentNode && element.parentNode instanceof HTMLElement ? element.parentNode : null;
    if (parentNode) {
      const nodes = element.parentNode.childNodes;
      // eslint-disable-next-line no-restricted-syntax
      for (const node of nodes) {
        const toArray = Array.from(node.parentNode.childNodes);
        const index = toArray.indexOf(element);
        if (node === element) {
          object.before2 = toArray[index - 2] || null;
          object.before = toArray[index - 1] || null;
          object.after = toArray[index + 1] || null;
          object.after2 = toArray[index + 2] || null;
        }
      }
    }

    return object;
  };

  // Check if control keys are pressed
  const isControlsKeyPressed = (e) => e.ctrlKey || e.metaKey || e.altKey || e.shiftKey;

  const isLetterOrSpecialKey = (e) => {
    // Extended regular expression to include Cyrillic letters
    // This covers the range from А to я, including ё (U+0410 to U+044F, U+0451)
    const letterRegex = /^[a-zA-Zа-яА-ЯёЁ]$/;

    // Array of keys that trigger selection disappearance
    const specialKeys = ['Enter', 'Backspace', 'Delete'];

    // Check if the key is a letter (including Cyrillic) or one of the special keys
    if (letterRegex.test(e.key) || specialKeys.includes(e.key)) {
      return true;
    }

    return false;
  };

  const isOperatorKey = (e) => {
    const specialKeys = [
      '.', ',', ';', ':', '!', '?', '"', "'", '(', ')', '[', ']', '{', '}', '<', '>', '/', '\\', '|', '@', '#', '$', '%', '^', '&', '*', '-', '_', '+', '=', '~', '`',
    ];

    return specialKeys.includes(e.key);
  };

  const selectCodeBlock = () => {
    const selectedNode = editor.selection.getNode();
    const nearestSiblings = getNearestSiblings(selectedNode);
    const nearestSiblingsAfter = (nearestSiblings.after || {}).tagName;

    // Clicking on pre element -> set cursor to the end of the code
    if (selectedNode.tagName === 'PRE') {
      const code = selectedNode.querySelector('code');
      vm.setCursorLocationToEnd(code);

      return;
    }

    // Clicking before/after pre element (on the same row) -> set cursor to the end of the code
    if (codeMode && selectedNode.tagName === 'P' && selectedNode.dataset.mceCaret
      && ['before', 'after'].includes(selectedNode.dataset.mceCaret)
      && nearestSiblingsAfter !== 'BR') {
      const direction = selectedNode.dataset.mceCaret === 'after' ? 'before' : 'after';

      if (nearestSiblings[direction] && nearestSiblings[direction].tagName === 'PRE') {
        const pre = nearestSiblings[direction];
        const code = pre.querySelector('code');
        if (direction === 'before') {
          vm.setCursorLocationToEnd(code);
        } else {
          editor.selection.setCursorLocation(code, 0); // other than 0,1 makes frontend to crash
        }
      }
    }
  };

  const deleteProtectedBlankField = (direction) => {
    // This deletes the protected mceNonEditable blank spans
    let node = editor.selection.getNode();
    let blankField = null;

    // Selected node is a blank field
    if (node && node.classList.contains('blank-field')) {
      blankField = node;
    } else { // Set bookmark as reference node to look before/after it
      const deleteBM = editor.selection.getBookmark();
      const deleteBMElement = editor.bodyElement.querySelector(`#${deleteBM.id}_start`);
      node = deleteBMElement;
    }

    // Selected node have (before/after) blank field sibling
    const nodeSiblings = getNearestSiblings(node);
    if (!blankField) {
      blankField = nodeSiblings[direction] && nodeSiblings[direction] instanceof HTMLElement && nodeSiblings[direction].querySelector('.blank-field');
    }

    // Selected node have (before/after) sibling element that contains a blank field
    if (!blankField && nodeSiblings[direction] && nodeSiblings[direction] instanceof HTMLElement && nodeSiblings[direction].classList.contains('blank-field')) {
      blankField = nodeSiblings[direction];
    }

    // Selected node have (before/after) sibling element that is not direct neighbor and between node and blank field has a &#xFEFF; symbol
    // This special symbol (&#xFEFF;) is counted as a textNode and preventing deletion of the blank field
    const direction2 = direction === 'before' ? 'before2' : 'after2';
    if (!blankField && nodeSiblings[direction] && nodeSiblings[direction].nodeType === 3 && !nodeSiblings.before.outerHTML && nodeSiblings[direction2] && nodeSiblings[direction2] instanceof HTMLElement && nodeSiblings[direction2].classList.contains('blank-field')) {
      blankField = nodeSiblings[direction2];
    }

    if (blankField) {
      blankField.remove();
      editorElement.dispatchEvent(new Event('input'));

      // Clear undo history because it's not working properly when deleting the blank field and then redoing it
      editor.undoManager.clear();
      return true;
    }

    return false;
  };

  const languageIcon = '<label class="codeblock-select-icon mceNonEditable" data-language-label="Javascript"></label>';
  let closeIcon = '<svg class="codeblock-close-icon" fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 15"><path d="M12.4 4.4a.6.6 0 1 0-.9-.8l-3 3-3.1-3a.6.6 0 1 0-.9.8l3 3.1-3 3a.6.6 0 0 0 .9 1l3-3.1 3.1 3a.6.6 0 0 0 .9-.8l-3-3.1z" fill="#545C69"/></svg>';

  if (codeMode) {
    closeIcon = '';
  }

  const highlight = (codeBlock, bm) => {
    const preElement = codeBlock.outerHTML;
    const content = codeBlock.closest('.mce-content-body').innerHTML;
    const blankIds = [];

    codeBlock.innerHTML = codeBlock.innerHTML.replace(/<span data-mce-type="bookmark"(.+?)<\/span>/gm, '~bm~');
    codeBlock.innerHTML = codeBlock.innerHTML.replace(/<span data-id="(.+?)".+?>(.+?)<\/span>/gm, (m0, id, c) => {
      blankIds.push({ id, content: c });

      return `~blank-${id}~`;
    });

    const language = codeBlock.dataset.languageKey;

    let code = codeBlock.innerText;

    if (blankIds.length) {
      for (let i = 0; i < blankIds.length; i += 1) {
        code = code.replace(`~blank-${blankIds[i].id}~`, `<span data-id="${blankIds[i].id}" class="blank-field mceNonEditable" contenteditable="false">${blankIds[i].content || ''}</span>`);
      }
    }

    code = code.replaceAll('~bm~', `<span data-mce-type="bookmark" id="${bm.id}_start" data-mce-style="overflow:hidden;line-height:0px" style="overflow: hidden; line-height: 0px;"></span>`);

    const d2 = document.createElement('pre');
    d2.classList.add(`language-${language}`);
    d2.classList.add('codeblock');
    d2.classList.add('mceNonEditable');
    d2.setAttribute('data-keep-tags', 'span,br');
    d2.setAttribute('data-language-label', vm.codeLanguages[language]);
    d2.setAttribute('data-language-key', language);
    const label = `<label class="codeblock-select-icon mceNonEditable" data-language-label="${vm.codeLanguages[language]}"></label>`;

    if (language === 'html') {
      code = code.replace(/<span data-mce-type="bookmark"(.+?)<\/span>/gm, '').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }

    d2.innerHTML = `<code class="language-${language} mceEditable">${code}</code>${label}${closeIcon}`;

    Prism.highlightElement(d2.querySelector('code'));

    let imageSizes = [];

    if (type === 'qa-hub') {
      const images = editor.dom.select('img', editor.getBody());
      if (images && images.length > 0) {
        imageSizes = images.map((img) => ({
          src: img.src,
          width: window.getComputedStyle(img).width,
          height: window.getComputedStyle(img).height,
        }));
      }
    }

    editor.setContent(content.replace(preElement, d2.outerHTML));

    if (type === 'qa-hub') {
      const newImages = editor.dom.select('img', editor.getBody());
      if (newImages && newImages.length > 0) {
        newImages.forEach((newImg) => {
          const originalSize = imageSizes.find((img) => img.src === newImg.src);
          if (originalSize) {
            newImg.style.setProperty('width', originalSize.width, 'important');
            newImg.style.setProperty('height', originalSize.height, 'important');
          }
        });
      }
    }
  };

  editor.ui.registry.addIcon('codeblock', '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="10" viewBox="0 0 12 10" fill="none"><path d="M4.86327 9.79583C5.06012 9.85205 5.25303 9.73954 5.31603 9.52261L7.66638 1.30299C7.72544 1.08605 7.627 0.88518 7.4302 0.824921C7.23333 0.768676 7.04043 0.881165 6.97742 1.09811L4.63099 9.31773C4.57194 9.53467 4.66643 9.73556 4.86327 9.79583V9.79583ZM8.77664 8.33748L11.7372 5.60966C11.9222 5.44093 11.9183 5.17578 11.7372 5.01107L8.77664 2.28325C8.61518 2.12658 8.39076 2.13059 8.25302 2.28726C8.11521 2.44394 8.1388 2.66891 8.29629 2.81756L10.9971 5.30835L8.29629 7.80316C8.1388 7.95182 8.11521 8.17677 8.25302 8.33344C8.39076 8.49018 8.61518 8.49018 8.77664 8.33749V8.33748ZM3.51683 8.34153C3.68218 8.49415 3.90265 8.49018 4.04045 8.33344C4.17824 8.17677 4.15855 7.95586 4.00107 7.80721L1.30031 5.31238L4.00107 2.81756C4.15855 2.66891 4.17823 2.44797 4.04045 2.28726C3.90265 2.13059 3.68218 2.13059 3.51683 2.28325L0.556226 5.01107C0.375126 5.17979 0.379067 5.44495 0.556226 5.60966L3.51683 8.34153Z" fill="#000C1F" stroke="#000C1F" stroke-width="0.164554"/></svg>');

  editor.ui.registry.addToggleButton('codeblock', {
    icon: 'codeblock',
    tooltip: 'Codeblock',
    active: true,
    disabled: false,
    onAction() {
      if (editor.selection.getNode().closest('pre')) {
        return;
      }
      editor.focus();
      let content = editor.selection.getContent().length ? `&nbsp;${editor.selection.getContent()}` : '';
      let id = `codeblock-${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;

      if (type === 'qa-hub') {
        if (content === '') {
          editor.insertContent(`<pre id="${id}" data-language-label="Javascript" data-keep-tags="span,br" data-language-key="javascript" class="codeblock language-javascript mceNonEditable"><code class="language-javascript mceEditable">${content}</code>${languageIcon}${closeIcon}</pre>&nbsp;`);
        } else {
          id = `inline_code-${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;
          content = editor.selection.getContent();
          editor.insertContent(`<code class="inline-code" id="${id}">${content}</code>&nbsp;`);
        }
      } else {
        const cookieLanguage = cookies.get('codeblock_language') || 'javascript';

        const language = vm.codeLanguages[cookieLanguage];

        const languageLabel = `<label class="codeblock-select-icon mceNonEditable" data-language-label="${language}"></label>`;

        editor.insertContent(`<pre id="${id}" data-language-label="${cookieLanguage || 'javascript'}" data-keep-tags="span,br" data-language-key="${cookieLanguage || 'javascript'}" class="codeblock language-${cookieLanguage || 'javascript'} mceNonEditable"><code class="language-${cookieLanguage || 'javascript'} mceEditable">${content}</code>${languageLabel}${closeIcon}</pre>&nbsp;`);
      }
      setTimeout(() => {
        const pre = editor.bodyElement.querySelector(`pre#${id}`);
        const code = pre.querySelector('code');

        vm.setCursorLocationToEnd(code);

        setTimeout(() => {
          // Highlight code
          const bm = editor.selection.getBookmark();
          const bmElement = editor.bodyElement.querySelector(`#${bm.id}_start`);
          highlight(pre, bm);
          vm.setCursorLocationToEnd(bmElement);
          editor.selection.moveToBookmark(bm);
        });
      }, 200);
    },
    onSetup(api) {
      function nodeChangeHandler() {
        setTimeout(() => {
          const selectedNode = editor.selection && editor.selection.getNode();

          if (selectedNode) {
            const disabled = (selectedNode.tagName === 'CODE' && !selectedNode.closest('pre')) || vm.isActiveKeyboard || selectedNode.tagName === 'IMG';
            api.setDisabled(disabled);

            api.setActive(selectedNode.closest('pre'));
          }
        });
      }

      editor.on('NodeChange', nodeChangeHandler);
    },
  });
  let isPaste = false;
  editor.on('keydown', (e) => {
    isPaste = e.key === 'v' && (e.ctrlKey || e.metaKey);
  });

  // Stopped for now to determine the best way to handle this
  editor.on('keyup', () => {
    selectCodeBlock(editor);
    const pre = editor.selection.getNode().closest('pre');

    if (isPaste && pre) {
      const bm = editor.selection.getBookmark();
      const bmElement = editor.bodyElement.querySelector(`#${bm.id}_start`);
      highlight(pre, bm);
      vm.setCursorLocationToEnd(bmElement);
      editor.selection.moveToBookmark(bm);
      editor.fire('change');
    }
  });
  editor.on('paste', (e) => {
    const codeBlock = editor.selection.getNode().closest('pre');
    if (codeBlock) {
      e.preventDefault();
      const clipboardData = e.clipboardData || window.clipboardData;
      const pasteContent = clipboardData.getData('text');

      const bm = editor.selection.getBookmark();
      const range = editor.selection.getRng();

      setTimeout(() => {
      // Create a text node with the pasted content
        const textNode = document.createTextNode(pasteContent);
        range.deleteContents(); // Delete the current selection contents
        range.insertNode(textNode); // Insert the text node at the current selection

        const selection = window.getSelection();
        const newRange = document.createRange();

        editor.selection.moveToBookmark(bm);

        newRange.setStartAfter(textNode);
        newRange.setEndAfter(textNode);
        newRange.collapse(true); // Collapse the range to a single point

        selection.removeAllRanges();
        selection.addRange(newRange);
      });
    }
  });

  // Prevent the cursor from moving outside the codeblock
  editor.on('click', () => {
    selectCodeBlock(editor);
  });

  editor.on('keydown', (e) => {
    // Handle Ctrl + A to select all content within the code block
    if (e.ctrlKey && e.key === 'a') {
      const codeBlock = editor.selection.getNode().closest('pre');

      if (codeBlock) {
        e.preventDefault();
        e.stopPropagation();

        // Select all content within the code block
        const code = codeBlock.querySelector('code');

        if (code) {
          const range = document.createRange();
          range.selectNodeContents(code);
          const selection = window.getSelection();
          selection.removeAllRanges();
          selection.addRange(range);
        }

        return;
      }
    }

    let codeBlock = editor.selection.getNode().closest('pre');
    const isCopy = e.key === 'c' && (e.ctrlKey || e.metaKey);
    const isCut = e.key === 'x' && (e.ctrlKey || e.metaKey);

    if (isCopy && codeBlock) {
      return;
    }

    if (isCut && codeBlock) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      const selectedContent = editor.selection.getContent({ format: 'text' });
      if (selectedContent) {
        vm.$copy(selectedContent);
        editor.selection.setContent('');
      }

      return;
    }

    if (codeBlock) {
      e.stopImmediatePropagation();
      e.stopPropagation();
    }
    const navigating = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key);
    let bm = null;
    let bmEl = null;

    // Do not allow deleting codeblock if type is answer
    if (codeBlock && codeMode && e.key === 'Backspace') {
      const code = codeBlock.querySelector('code');
      if (code && code.innerText.trim() === '') {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
    }

    if (e.key === 'Delete' && codeBlock && !textMode) {
      // This deletes the protected mceNonEditable blank spans
      const deleted = deleteProtectedBlankField('after');
      if (deleted) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
    }

    // Delete only selected content on Backspace / Delete keys and prevent any further action
    const selectedContent = editor.selection.getContent();
    if (codeBlock && selectedContent && ['Backspace', 'Delete'].includes(e.key)) {
      e.preventDefault();
      e.stopPropagation();
      editor.selection.setContent('');
      return;
    }

    if (e.key === 'Tab') {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      editor.insertContent('&nbsp;&nbsp;');
      editor.focus();
    }

    if (e.key === 'Backspace') {
      // Check if there is codeblock before the caret
      if (!codeBlock) {
        const parentSiblings = getNearestSiblings(editor.selection.getNode());

        if (parentSiblings.before && parentSiblings.before.tagName === 'PRE') {
          bm = editor.selection.getBookmark();
          bmEl = editor.bodyElement.querySelector(`#${bm.id}_start`);
          const bmSiblings = getNearestSiblings(bmEl);

          if (bmSiblings.after && bmSiblings.after.tagName !== 'BR' && !bmSiblings.before) {
            e.preventDefault();
            e.stopPropagation();
            bmEl.remove();
            return;
          }

          bmEl.remove();
        }
      }

      // Delete codeblock if empty
      if (codeBlock && codeBlock.querySelector('code').innerText.trim() === '' && textMode) {
        let newBm = null;
        let newBmElement = null;
        const nearestSiblings = getNearestSiblings(codeBlock);
        const nearest = nearestSiblings.before || nearestSiblings.after;

        if (nearest) {
          editor.selection.setCursorLocation(nearest, 0);
          newBm = editor.selection.getBookmark();
          newBmElement = editor.bodyElement.querySelector(`#${newBm.id}_start`);
        }

        codeBlock.remove();

        if (newBm) {
          editor.selection.moveToBookmark(newBm);
          newBmElement.remove();
        }

        return;
      }

      // This deletes the protected mceNonEditable blank spans
      if (codeBlock && !textMode) {
        const deleted = deleteProtectedBlankField('before');
        if (deleted) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
      }
    }

    // Delete selected content properly before inserting newly pressed key
    const selected = editor.selection.getContent();
    if (selected && codeBlock && !navigating && isLetterOrSpecialKey(e) && !isControlsKeyPressed(e)) {
      editor.selection.setContent('');
    }

    // WORKS FINE
    // Return cursor position if it's outside of the current codeblock
    // - set caret position to transparent to prevent flickering
    // - always remove caret position
    if (['ArrowUp', 'ArrowDown'].includes(e.key) && codeBlock && !textMode) {
      hideCaret();
      const prevCodeBlock = codeBlock;
      const prevBookmark = editor.selection.getBookmark();
      e.stopPropagation();
      e.stopImmediatePropagation();

      setTimeout(() => {
        const codeblock = editor.selection.getNode().closest('pre');
        if (prevCodeBlock && prevBookmark && prevCodeBlock !== codeblock) {
          editor.selection.moveToBookmark(prevBookmark);
        } else {
          const prevBookmarkElement = editor.bodyElement.querySelector(`#${prevBookmark.id}_start`);
          if (prevBookmarkElement) {
            prevBookmarkElement.remove();
          }
        }

        showCaret();
      }, 10);

      return;
    }

    const willHighlight = codeBlock && !(e.keyCode === 90 && e.ctrlKey)
      && !['ArrowLeft', 'ArrowRight'].includes(e.key) && (isLetterOrSpecialKey(e) || isOperatorKey(e));

    codeBlock = editor.selection.getNode().closest('pre');
    if (willHighlight || (codeBlock && e.key === 'Enter')) {
      bm = editor.selection.getBookmark();
      bmEl = editor.bodyElement.querySelector(`#${bm.id}_start`);
    }

    // Enter key
    if (codeBlock && e.key === 'Enter') {
      // Shift + Enter
      if (e.shiftKey && ((vm.mode === 'general' || !vm.mode) && (vm.selectedMode === 'text' || !vm.selectedMode))) { // Add new Line after the codeblock if is in Text mode and inside codeblock
        editor.selection.moveToBookmark(bm);
        const p = document.createElement('p');
        const br = document.createElement('br');
        p.appendChild(br);
        codeBlock.parentNode.insertBefore(p, codeBlock.nextSibling);
        editor.selection.select(p);
        editor.selection.collapse();

        return;
      }

      // Enter
      // This fixes inserting new line inside token and cursor moves to the end of the previous line
      e.preventDefault();
      e.stopPropagation();

      setTimeout(() => {
        const nearestSiblings = getNearestSiblings(bmEl);
        const currentElement = editor.selection.getNode();
        if (currentElement.tagName === 'SPAN' && currentElement.classList.contains('token') && !nearestSiblings.after) {
          editor.selection.select(currentElement);
          editor.selection.collapse();
        }

        if (currentElement.tagName === 'CODE') {
          editor.execCommand('InsertLineBreak');
        } else {
          editor.execCommand('mceInsertNewLine');
        }
      });
    }

    // Excluding arrows because the highlight is broken when navigating inside the codeblock
    if (willHighlight) {
      highlight(codeBlock, bm);
      editor.selection.moveToBookmark(bm);
    }

    if (codeBlock && bmEl) {
      bmEl.remove();
    }

    // This needs to be tested
    vm.checkIfCodeBlockHasHorizontalScrollbar();
  });
};
